import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Tabs,
  Tab,
  IconButton,
  Button,
  ButtonGroup,
  useMediaQuery,
  useTheme,
  CardHeader,
  Tooltip,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Dialog,
  TextField,
  Menu,
  MenuItem,
} from '@mui/material';
import School from '@mui/icons-material/School';
import Add from '@mui/icons-material/Add';
import Share from '@mui/icons-material/Share';
import Group from '@mui/icons-material/Group';
import MoreVert from '@mui/icons-material/MoreVert';
import ViewModule from '@mui/icons-material/ViewModule';
import TableChart from '@mui/icons-material/TableChart';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useConfirm } from '../../shared/components/ConfirmProvider';
import { useAppDispatch, useAppSelector } from '../../shared/hooks/redux';
import { formatRelativeTime } from '../../shared/utils/dateUtils';
import { setIsMobile } from '../../shared/store/uiSlice';

import {
  useGetMyCoursesQuery,
  useGetTeamCoursesQuery,
  useCreateCourseMutation,
  useDeleteCourseMutation,
} from './coursesApi';
import { setViewMode, setCurrentTab, type CoursesTab } from './coursesSlice';
import SharedWithMeList from './components/SharedWithMeList';

import type { SharedCurriculumItem } from '@/shared/types/sharedCurriculum';
import { Course } from '@/shared/types/course';
import { PageSpinner } from '@/shared/components/PageSpinner';
import ShareDialog from '@/shared/components/ShareDialog';
import {
  useGetSharedWithMeQuery,
  useSetPublicCascadeMutation,
} from '@/features/permissions/permissionsApi';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';
import UserAvatar from '@/shared/components/UserAvatar';

const DEFAULT_TAB: CoursesTab = 'owned';

const MyCourses: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const teamManagementEnabled = isFeatureEnabled('teamManagement');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirm();
  // Redux state
  const { viewMode, currentTab } = useAppSelector((state) => state.courses);
  const { user } = useAppSelector((state) => state.auth);
  // Get current user info from auth
  const currentUserId = user?.id ?? '';
  //const currentUserName = user?.user_metadata?.name ?? ''
  //const currentUserEmail = user?.email ?? ''

  // Update global UI state for mobile
  useEffect(() => {
    dispatch(setIsMobile(isMobile));
  }, [isMobile, dispatch]);

  //console.log('Current user:', { id: currentUserId, name: currentUserName, email: currentUserEmail })

  // RTK Query hooks - only execute if we have a user ID
  const {
    data: myCourses = [],
    isLoading: myCoursesLoading,
    error: myCoursesError,
  } = useGetMyCoursesQuery(currentUserId, { skip: !currentUserId });
  const {
    data: sharedWithMe = [],
    isLoading: sharedWithMeLoading,
    error: sharedWithMeError,
  } = useGetSharedWithMeQuery(undefined, { skip: !currentUserId });
  const {
    data: teamCourses = [],
    isLoading: teamCoursesLoading,
    error: teamCoursesError,
  } = useGetTeamCoursesQuery(currentUserId, { skip: !currentUserId || !teamManagementEnabled });

  // Mutations
  const [createCourse] = useCreateCourseMutation();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: CoursesTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newValue === DEFAULT_TAB) {
        next.delete('tab');
      } else {
        next.set('tab', newValue);
      }
      return next;
    });
  };

  const handleViewModeChange = (mode: 'grid' | 'table') => {
    dispatch(setViewMode(mode));
  };

  const handleCourseClick = (courseId: string) => {
    navigate(`/curriculum/courses/${courseId}`);
  };

  const handleSharedItemClick = (item: SharedCurriculumItem) => {
    if (item.object_type === 'course') {
      navigate(`/curriculum/courses/${item.object_id}`);
      return;
    }

    if (item.object_type === 'unit') {
      if (item.has_course_access && item.course_id) {
        navigate(`/curriculum/courses/${item.course_id}/units/${item.object_id}`);
      } else {
        navigate(`/curriculum/units/${item.object_id}`);
      }
      return;
    }

    // Lesson: build the deepest accessible hierarchy URL
    if (item.has_course_access && item.course_id && item.has_unit_access && item.unit_id) {
      navigate(
        `/curriculum/courses/${item.course_id}/units/${item.unit_id}/lessons/${item.object_id}`
      );
    } else if (item.has_unit_access && item.unit_id) {
      navigate(`/curriculum/units/${item.unit_id}/lessons/${item.object_id}`);
    } else {
      navigate(`/curriculum/lessons/${item.object_id}`);
    }
  };

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Menu state for MoreVert
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const menuOpen = Boolean(menuAnchorEl);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCourseId, setShareCourseId] = useState<string | null>(null);

  // Delete mutation
  const [deleteCourse, { isLoading: deletingCourse }] = useDeleteCourseMutation();
  const [setPublicCascade] = useSetPublicCascadeMutation();

  const shareCourse = useMemo(
    () =>
      shareCourseId
        ? (myCourses?.find((c) => c.id === shareCourseId) ??
          teamCourses?.find((c) => c.id === shareCourseId))
        : undefined,
    [shareCourseId, myCourses, teamCourses]
  );
  const pageTitle =
    currentTab === 'shared'
      ? 'Shared with Me'
      : currentTab === 'team'
        ? 'Team Courses'
        : 'My Courses';

  useEffect(() => {
    if (!teamManagementEnabled && currentTab === 'team') {
      dispatch(setCurrentTab(DEFAULT_TAB));
    }
  }, [currentTab, dispatch, teamManagementEnabled]);

  // Sync Redux tab state from URL — also handles back/forward navigation
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (
      tabParam === 'shared' ||
      tabParam === 'owned' ||
      (tabParam === 'team' && teamManagementEnabled)
    ) {
      dispatch(setCurrentTab(tabParam as CoursesTab));
    } else {
      dispatch(setCurrentTab(DEFAULT_TAB));
    }
  }, [searchParams, dispatch, teamManagementEnabled]);

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setNewTitle('');
    setNewDescription('');
    setErrorMsg(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setErrorMsg(null);
  };

  const handleCreateCourse = async () => {
    if (!newTitle.trim()) {
      setErrorMsg('Course title is required.');
      return;
    }
    setCreating(true);
    setErrorMsg(null);
    try {
      const newCourse = await createCourse({
        title: newTitle,
        description: newDescription,
        owner_id: currentUserId,
      }).unwrap();
      setDialogOpen(false);
      navigate(`/curriculum/courses/${newCourse.id}`);
    } catch (error) {
      console.error('Create course error:', error);
      setErrorMsg('Failed to create course. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, courseId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedCourseId(courseId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedCourseId(null);
  };

  const handleDeleteCourse = async () => {
    if (selectedCourseId) {
      const confirmed = await confirm({
        title: 'Delete Course',
        message:
          'Are you sure you want to delete this course? This also deletes its units, lessons, and resources. This action cannot be undone.',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
      });
      if (confirmed) {
        try {
          await deleteCourse(selectedCourseId).unwrap();
          handleMenuClose();
        } catch (error) {
          setErrorMsg('Failed to delete course. Please try again.');
        }
      }
    }
  };
  const handleOpenShare = () => {
    setShareCourseId(selectedCourseId);
    setShareOpen(true);
    handleMenuClose();
  };
  const CourseCard: React.FC<{ course: Course }> = ({ course }) => (
    <Card sx={{ height: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
        onClick={() => handleCourseClick(course.id)}
      >
        <CardHeader
          title={course.title}
          action={
            <IconButton
              size="small"
              sx={{ color: 'primary.contrastText' }}
              onClick={(e) => handleMenuOpen(e, course.id)}
            >
              <MoreVert />
            </IconButton>
          }
          sx={{
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '& .MuiCardHeader-title': { fontSize: '1.25rem' },
          }}
        />

        {/* Make the middle section grow to push footer down */}
        <CardContent sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <Typography variant="body2" color="text.secondary">
            {course.description}
          </Typography>
        </CardContent>

        {/* Footer row */}
        <Box
          component="footer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Tooltip title={`Created by ${course.owner_name}`}>
            <UserAvatar
              userId={course.owner_id}
              name={course.owner_name}
              src={course.owner_avatar_url}
            />
          </Tooltip>

          <Typography variant="caption" color="text.secondary">
            {formatRelativeTime(course.updated_at)}
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );

  const renderCourseContent = (
    courses: Course[],
    isLoading: boolean,
    error: unknown,
    emptyMessage: string,
    showCreateCta = false
  ) => {
    if (isLoading) {
      return <PageSpinner />;
    }

    if (error) {
      console.error('Course loading error:', error);
      return (
        <Box sx={{ py: 4 }}>
          <Alert severity="error">Failed to load courses. Please try again.</Alert>
        </Box>
      );
    }

    if (courses.length === 0) {
      console.log('No courses found for current query.');
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No courses found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
          {showCreateCta && currentTab !== 'shared' && (
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ mt: 2 }}
              onClick={handleOpenDialog}
            >
              Create Course
            </Button>
          )}
        </Box>
      );
    }

    if (viewMode === 'table') {
      return (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                <TableCell>Name</TableCell>
                {!isMobile && <TableCell>Description</TableCell>}
                {!isMobile && <TableCell>Owner</TableCell>}
                {!isMobile && <TableCell>Last Modified</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {courses.map((course) => (
                <TableRow
                  key={course.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleCourseClick(course.id)}
                >
                  <TableCell>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {course.title}
                    </Typography>
                  </TableCell>
                  {!isMobile && (
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {course.description || (
                          <span style={{ color: '#aaa', fontStyle: 'italic' }}>No description</span>
                        )}
                      </Typography>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={`Created by ${course.owner_name}`}>
                          <UserAvatar
                            userId={course.owner_id}
                            name={course.owner_name}
                            src={course.owner_avatar_url}
                            size={28}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatRelativeTime(course.updated_at)}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Tooltip title="More actions">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, course.id)}>
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 3,
        }}
      >
        {courses.map((course) => (
          <CourseCard key={course.id} course={course} />
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0 }}>
          <Typography variant="h2" component="h1">
            {pageTitle}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {!isMobile && currentTab !== 'shared' && (
              <ButtonGroup variant="outlined" size="small">
                <Button
                  onClick={() => handleViewModeChange('grid')}
                  variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                >
                  <ViewModule />
                </Button>
                <Button
                  onClick={() => handleViewModeChange('table')}
                  variant={viewMode === 'table' ? 'contained' : 'outlined'}
                >
                  <TableChart />
                </Button>
              </ButtonGroup>
            )}
            {currentTab !== 'shared' && (
              <Button variant="contained" startIcon={<Add />} onClick={handleOpenDialog}>
                {isMobile ? 'Create' : 'Create Course'}
              </Button>
            )}
          </Box>
        </Box>

        {/* Tabs Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab
              value="owned"
              icon={<School />}
              label="My Courses"
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab
              value="shared"
              icon={<Share />}
              label="Shared with Me"
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            {teamManagementEnabled && (
              <Tab
                value="team"
                icon={<Group />}
                label="Team Courses"
                iconPosition="start"
                sx={{ textTransform: 'none' }}
              />
            )}
          </Tabs>
        </Box>

        {/* Course Content */}
        <Box>
          {currentTab === 'owned' &&
            renderCourseContent(
              myCourses,
              myCoursesLoading,
              myCoursesError,
              'Create your first course to get started',
              true
            )}
          {currentTab === 'shared' && (
            <SharedWithMeList
              items={sharedWithMe}
              isLoading={sharedWithMeLoading}
              error={sharedWithMeError}
              onOpenItem={handleSharedItemClick}
            />
          )}
          {teamManagementEnabled &&
            currentTab === 'team' &&
            renderCourseContent(
              teamCourses,
              teamCoursesLoading,
              teamCoursesError,
              'No team courses available yet'
            )}
        </Box>

        {/* MoreVert Menu for course actions */}
        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={handleDeleteCourse}
            disabled={deletingCourse}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete Course
          </MenuItem>
          <MenuItem onClick={handleOpenShare} disabled={!selectedCourseId}>
            <Share fontSize="small" sx={{ mr: 1 }} />
            Share
          </MenuItem>
        </Menu>

        {/* Create Course Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="xs">
          <Box
            component="form"
            sx={{ p: 3 }}
            onSubmit={(e) => {
              e.preventDefault();
              handleCreateCourse();
            }}
          >
            <Typography variant="h6" mb={2}>
              Create New Course
            </Typography>
            <TextField
              label="Course Title*"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              fullWidth
              autoFocus
              autoComplete="off"
              margin="normal"
              disabled={creating}
            />
            <TextField
              label="Description"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              fullWidth
              autoComplete="off"
              multiline
              minRows={2}
              margin="normal"
              disabled={creating}
            />
            {errorMsg && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMsg}
              </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button onClick={handleCloseDialog} disabled={creating}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={creating}>
                {creating ? <CircularProgress size={20} /> : 'Create Course'}
              </Button>
            </Box>
          </Box>
        </Dialog>
      </Container>
      {shareCourseId && (
        <ShareDialog
          open={shareOpen}
          onClose={() => {
            setShareOpen(false);
            setShareCourseId(null);
          }}
          objectId={shareCourseId}
          objectType={'course'}
          ownerId={shareCourse?.owner_id ?? ''}
          isPublic={shareCourse?.is_public}
          onTogglePublic={(val) =>
            setPublicCascade({
              object_type: 'course',
              object_id: shareCourseId,
              is_public: val,
            }).unwrap()
          }
          shareUrl={`${window.location.origin}/curriculum/courses/${shareCourseId}`}
        />
      )}
    </Box>
  );
};

export default MyCourses;
